import {
  Children,
  createElement,
  isValidElement,
  useCallback,
  useMemo,
} from 'react'
import type {
  CSSProperties,
  ComponentProps,
  ComponentType,
  JSX,
  ReactNode,
} from 'react'
import { clsx } from 'clsx'
import useId from '../../../../shared/helpers/useId'
import {
  ToggleButton,
  Dropdown,
  Radio,
  Autocomplete,
  HelpButton,
} from '../../../../components'
import type { FieldOptionProps as OptionFieldProps } from '../Option'
import OptionField from '../Option'
import { useFieldProps } from '../../hooks'
import type { ReturnAdditional } from '../../hooks/useFieldProps'
import { checkForError } from '../../hooks/useFieldProps'
import { pickSpacingProps } from '../../../../components/flex/utils'
import type { FieldBlockProps, FieldBlockWidth } from '../../FieldBlock'
import FieldBlock from '../../FieldBlock'
import type { FieldProps, Path } from '../../types'
import type { FormStatusText } from '../../../../components/FormStatus'
import type { AutocompleteAllProps } from '../../../../components/Autocomplete'
import type { DropdownAllProps } from '../../../../components/Dropdown'
import type { HelpProps } from '../../../../components/help-button/HelpButtonInline'
import type {
  DrawerListDataArrayObjectStrict,
  DrawerListProps,
} from '../../../../fragments/DrawerList'
import useDataValue from '../../hooks/useDataValue'
import type { FormError } from '../../utils'
import type { RadioProps } from '../../../../components/Radio'
import type { ToggleButtonProps } from '../../../../components/ToggleButton'
import type { RadioGroupProps } from '../../../../components/radio/RadioGroup'
import type { ToggleButtonGroupProps } from '../../../../components/toggle-button/ToggleButtonGroup'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

type IOption = {
  title: string | ReactNode
  value: number | string
  status: FormStatusText
}
export type Data = Array<
  {
    value: number | string
    title: ReactNode
    text?: ReactNode
    disabled?: boolean
    style?: CSSProperties
    [key: string]: any
  } & Partial<DrawerListDataArrayObjectStrict>
>

type RenderSelectionChildren = (params: {
  value: IOption['value']
  options: FieldSelectionProps['data']
}) => ReactNode

type DrawerListChangeParams = {
  data?: DrawerListDataArrayObjectStrict | string | null
  value?: IOption['value']
}

type DrawerListVisibilityParams = {
  data?: DrawerListDataArrayObjectStrict | string | null
}

export type FieldSelectionProps = FieldProps<IOption['value']> & {
  /**
   * Choice of UI feature. Can be: `dropdown`, `autocomplete`, `button`, `radio`.
   */
  variant?: 'dropdown' | 'autocomplete' | 'radio' | 'button'

  /**
   * `small`, `medium` or `large` for predefined standard widths, `stretch` for fill available width.
   */
  width?: FieldBlockWidth

  /**
   * Layout for the list of options. Can be `horizontal` or `vertical`.
   */
  optionsLayout?: 'horizontal' | 'vertical'

  /**
   * Transform the displayed selection for Dropdown and Autocomplete variant. Use it to display a different value than the one in the data set. The first parameter is the properties of the Option component or data item. You can return a `React.ReactNode` that will be displayed in the selection.
   */
  transformSelection?: (props: OptionFieldProps) => ReactNode

  /**
   * The path to the context data (Form.Handler). The context data object needs to have a `value` and a `title` property. The generated options will be placed above given JSX based children. When `children` is a function, the generated options are instead provided as `options` to the function.
   */
  dataPath?: Path

  /**
   * Data to be used for the component. The object needs to have a `value` and a `title` property. Provide the Dropdown or Autocomplete data in the format documented here: [Dropdown](/uilib/components/dropdown) and [Autocomplete](/uilib/components/autocomplete) documentation.
   */
  data?: Data

  /**
   * An array of group titles for the list items. Only the first group can be `undefined`.
   */
  groups?: ReactNode[]
  /**
   * Forward any additional properties to the [Autocomplete](/uilib/components/autocomplete/) component. `onType` will additionally provide the `value` parameter with `emptyValue` support in addition to the internal `dataContext`.
   */
  autocompleteProps?: AutocompleteAllProps

  /**
   * Forward any additional properties to the [Dropdown](/uilib/components/dropdown/) component.
   */
  dropdownProps?: DropdownAllProps

  /**
   * The sizes you can choose are `small` (1.5rem), `default` (2rem), `medium` (2.5rem) and `large` (3rem). Defaults to `default` / `null`. Also, if you define a number like `size="2"` then it will be forwarded as the input element attribute. Consider rather setting field sizes with [Form.Appearance](/uilib/extensions/forms/Form/Appearance/).
   */
  size?:
    | ToggleButtonGroupProps['size']
    | RadioGroupProps['size']
    | AutocompleteAllProps['size']
    | DropdownAllProps['size']

  /**
   * For providing [Field.Option](/uilib/extensions/forms/base-fields/Option/) components and other children. Can also be a render function that receives `{ value, options }`, where `options` are from `data` or `dataPath` and may include additional custom properties.
   */
  children?: ReactNode | RenderSelectionChildren
}

function Selection(props: FieldSelectionProps) {
  const clearValue = `clear-option-${useId()}`

  const {
    id,
    className,
    variant = 'dropdown',
    layout = 'vertical',
    optionsLayout = 'vertical',
    placeholder,
    value,
    info,
    warning,
    error,
    hasError,
    disabled,
    size,
    emptyValue,
    width,
    htmlAttributes,
    setHasFocus,
    handleChange,
    setDisplayValue,
    transformSelection,
    data,
    groups,
    dataPath,
    children,
    additionalArgs,

    // - Autocomplete and Dropdown specific props
    autocompleteProps,
    dropdownProps,
  } = useFieldProps(props)

  const { value: dataPathValue } = useDataValue(dataPath, undefined, {
    pathType: 'absolute',
  })
  const dataList = dataPath ? dataPathValue : data
  const hasRenderPropChildren = typeof children === 'function'
  const renderedChildren = useMemo(() => {
    return resolveChildren(children, value, dataList)
  }, [children, dataList, value])

  const handleDrawerListChange = useCallback(
    ({ data, value }: DrawerListChangeParams) => {
      const selectedKey =
        typeof data === 'object' && data ? data.selectedKey : value
      handleChange?.(
        !selectedKey || selectedKey === clearValue
          ? emptyValue
          : selectedKey,
        { data }
      )
    },
    [handleChange, emptyValue, clearValue]
  )

  const onChangeHandler = useCallback(
    ({ value }) => {
      handleChange?.(value === undefined ? emptyValue : value)
    },
    [handleChange, emptyValue]
  )

  // Specific handleShow and handleHide because Dropdown preserve the initially received callbacks, so changes
  // due to `useCallback` usage will have no effect, leading to useFieldProps's handleFocus and handleBlur sending out old
  // copies of value as arguments.
  const handleShow = useCallback(
    ({ data }: DrawerListVisibilityParams) => {
      setHasFocus(
        true,
        typeof data === 'object' && data ? data.selectedKey : undefined
      )
    },
    [setHasFocus]
  )

  const handleHide = useCallback(
    ({ data }: DrawerListVisibilityParams) => {
      setHasFocus(
        false,
        typeof data === 'object' && data ? data.selectedKey : undefined
      )
    },
    [setHasFocus]
  )

  const cn = clsx(
    'dnb-forms-field-selection',
    `dnb-forms-field-selection__variant--${variant}`,
    `dnb-forms-field-selection--layout-${layout}`,
    `dnb-forms-field-selection--options-layout--${optionsLayout}`,
    className
  )

  const fieldBlockProps: FieldBlockProps = {
    forId: id,
    className: cn,
    disableStatusSummary: true,
    ...pickSpacingProps(props),
  }

  const onType = props?.autocompleteProps?.onType
  const onTypeAutocompleteHandler = useCallback(
    (event) => {
      if (typeof onType === 'function') {
        const { value } = event
        onType({
          ...event,
          ...additionalArgs,
          value: value === '' ? emptyValue : value,
        })
      }
    },
    [additionalArgs, emptyValue, onType]
  )

  switch (variant) {
    case 'radio':
    case 'button': {
      const Component = (
        variant === 'radio' ? Radio : ToggleButton
      ) as typeof Radio & typeof ToggleButton

      const items = renderRadioItems({
        id,
        value,
        variant,
        info,
        warning,
        htmlAttributes,
        children: renderedChildren,
        dataList: hasRenderPropChildren ? undefined : dataList,
        hasError,
        iterateOverItems: ({ value: v, label }) => {
          if (v === value) {
            setDisplayValue(label)
          }
        },
      })

      const additionalFieldBlockProps: FieldBlockProps = {
        asFieldset: hasRenderPropChildren || Children.count(items) > 1,
        fieldsetRole: variant === 'radio' ? 'radiogroup' : 'group',
      }
      if (!size) {
        additionalFieldBlockProps.labelHeight = 'small'
      }
      if (width) {
        if (width === 'stretch') {
          additionalFieldBlockProps.width = width
        }
        additionalFieldBlockProps.contentWidth = width
      }

      return (
        <FieldBlock {...fieldBlockProps} {...additionalFieldBlockProps}>
          <Component.Group
            size={
              size as ToggleButtonGroupProps['size'] &
                RadioGroupProps['size']
            }
            className={cn}
            layoutDirection={
              optionsLayout === 'horizontal' ? 'row' : 'column'
            }
            disabled={disabled}
            onChange={onChangeHandler}
            value={String(value ?? '')}
          >
            {items}
          </Component.Group>
        </FieldBlock>
      )
    }

    case 'autocomplete':
    case 'dropdown': {
      const data = renderDropdownItems(
        hasRenderPropChildren ? undefined : dataList,
        transformSelection
      )
        .concat(makeOptions(renderedChildren, transformSelection))
        .filter(Boolean)
      const displayValue = data.find(
        (item) => item.selectedKey === value
      )?.content
      setDisplayValue(displayValue)

      const sharedProps: Omit<
        AutocompleteAllProps & DropdownAllProps,
        'ref'
      > = {
        id,
        listClass: 'dnb-forms-field-selection__list',
        portalClass: 'dnb-forms-field-selection__portal',
        title: placeholder,
        value: String(value ?? ''),
        status:
          hasError || checkForError([error, info, warning])
            ? 'error'
            : undefined,
        disabled,
        ...htmlAttributes,
        data,
        groups,
        size,
        onChange: handleDrawerListChange,
        onOpen: handleShow,
        onClose: handleHide,
        stretch: true,
      }

      const specificFieldBlockProps: FieldBlockProps = {
        width: width === 'stretch' ? width : undefined,
        contentWidth: width ?? 'large',
      }

      return (
        <FieldBlock {...fieldBlockProps} {...specificFieldBlockProps}>
          {variant === 'autocomplete' ? (
            <Autocomplete
              {...sharedProps}
              {...autocompleteProps}
              value={
                autocompleteProps?.preventSelection ? undefined : value
              }
              onType={onTypeAutocompleteHandler}
              data={
                !props.data &&
                !props.dataPath &&
                autocompleteProps?.mode === 'async'
                  ? undefined
                  : data
              }
              selectAll
            />
          ) : (
            <Dropdown {...sharedProps} {...dropdownProps} />
          )}
        </FieldBlock>
      )
    }
  }
}

function resolveChildren(
  children: FieldSelectionProps['children'],
  value: FieldSelectionProps['value'],
  options: FieldSelectionProps['data']
) {
  if (typeof children === 'function') {
    return children({ value, options })
  }

  return children
}

type OptionProps = ComponentProps<
  (props: {
    value: FieldSelectionProps['value']
    error: Error | FormError | undefined
    help: HelpProps
    title: ReactNode
    children: ReactNode
    size?: ToggleButtonProps['size'] | RadioProps['size']
  }) => JSX.Element
>

function renderRadioItems({
  id,
  value: valueProp,
  variant,
  info,
  warning,
  htmlAttributes,
  children,
  dataList,
  hasError,
  iterateOverItems,
}: {
  id: string
  value: FieldSelectionProps['value']
  variant: FieldSelectionProps['variant']
  info: FieldSelectionProps['info']
  warning: FieldSelectionProps['warning']
  htmlAttributes: FieldSelectionProps['htmlAttributes']
  children: ReactNode
  dataList?: Data
  hasError: ReturnAdditional<FieldSelectionProps['value']>['hasError']
  iterateOverItems?: (item: {
    value: FieldSelectionProps['value']
    label: ReactNode
  }) => void
}) {
  const optionsCount = countOptions(children) + (dataList?.length || 0)

  const createOption = (props: OptionProps, i: number) => {
    const { value, title, children, error, help, size, ...rest } = props

    const label = title ?? children
    const suffix = help ? (
      <HelpButton size="small" title={help.title}>
        {help.content}
      </HelpButton>
    ) : undefined

    iterateOverItems?.({ value, label })

    const Component = (
      variant === 'radio' ? Radio : ToggleButton
    ) as typeof Radio & typeof ToggleButton

    return (
      <Component
        id={optionsCount === 1 ? id : undefined}
        key={`option-${i}-${id}`}
        label={variant === 'radio' ? label : undefined}
        text={variant === 'button' ? label : undefined}
        role="radio"
        value={String(value ?? valueProp) || undefined}
        status={
          hasError || checkForError([error, info, warning])
            ? 'error'
            : undefined
        }
        suffix={suffix}
        size={size}
        {...htmlAttributes}
        {...rest}
      />
    )
  }

  return [
    ...(dataList || []).map((props, i) => {
      return createOption(props as OptionProps, i)
    }),
    ...(mapOptions(children, { createOption }) || []),
  ].filter(Boolean)
}

export function countOptions(children: ReactNode): number {
  let count = 0

  Children.forEach(children, (child) => {
    if (isValidElement(child)) {
      if (child.type === OptionField) {
        count++
      } else if ((child.props as { children?: ReactNode }).children) {
        count += countOptions(
          (child.props as { children?: ReactNode }).children
        )
      }
    }
  })

  return count
}

export function mapOptions(
  children: ReactNode,
  {
    createOption,
  }: { createOption(props: OptionProps, i: number): ReactNode }
) {
  return Children.map(children, (child, i) => {
    if (isValidElement<OptionProps>(child)) {
      if (child.type === OptionField) {
        return createOption(child.props, i)
      }

      if (child.props.children) {
        const nestedChildren = mapOptions(child.props.children, {
          createOption,
        })
        return createElement(
          child.type as ComponentType<OptionProps>,
          child.props,
          nestedChildren
        )
      }
    }

    return child
  })
}

export function makeOptions<T = DrawerListProps['data']>(
  children: ReactNode,
  transformSelection?: FieldSelectionProps['transformSelection']
): T {
  return Children.map(children, (child) => {
    if (child?.['props']?.children?.type === OptionField) {
      child = child['props'].children
    }

    if (isValidElement(child) && child.type === OptionField) {
      const props = child.props as OptionFieldProps
      const title = props.title ?? props.children ?? <em>Untitled</em>
      const content = props.text ? [title, props.text] : title
      const selectedValue = transformSelection
        ? transformSelection(props)
        : undefined
      const selectedKey = String(props.value ?? '')

      const disabled = props.disabled
      const style = props.style
      const groupIndex = props.groupIndex

      return {
        selectedKey,
        selectedValue,
        content,
        disabled,
        style,
        groupIndex,
      }
    }

    // For other children, just show them as content
    if (child) {
      return {
        content: child,
      }
    }

    return undefined
  }) as T
}

function renderDropdownItems(
  data: Data,
  transformSelection?: FieldSelectionProps['transformSelection']
) {
  return (
    data?.map((props) => {
      const { value, title, text, disabled, style, ...rest } = props
      return {
        selectedKey: value,
        content: (text ? [title, text] : title) || <em>Untitled</em>,
        selectedValue: transformSelection
          ? transformSelection(props)
          : undefined,
        disabled,
        style,
        ...rest,
      }
    }) || []
  )
}

withComponentMarkers(Selection, {
  _supportsSpacingProps: true,
})

export default Selection
